import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronUpIcon, ChevronDownIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

type SortField = 'category' | 'responsible' | 'description' | 'destination' | 'amount';
type SortDirection = 'asc' | 'desc';

interface TransactionTableHeaderProps {
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
  showActions?: boolean;
}

export const TransactionTableHeader: React.FC<TransactionTableHeaderProps> = ({
  sortField,
  sortDirection,
  onSort,
  showActions = true,
}) => {
  const { t } = useTranslation();

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ChevronUpIcon className="ml-1 inline h-4 w-4" />
    ) : (
      <ChevronDownIcon className="ml-1 inline h-4 w-4" />
    );
  };

  return (
    <thead className="bg-slate-50 dark:bg-surface-800">
      <tr>
        <th
          className="cursor-pointer px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400 sm:px-6"
          onClick={() => onSort('description')}
        >
          {t('transaction.description')} {renderSortIcon('description')}
        </th>
        <th
          className="cursor-pointer px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400 sm:px-6"
          onClick={() => onSort('responsible')}
        >
          {t('transaction.responsible')} {renderSortIcon('responsible')}
        </th>
        <th
          className="hidden cursor-pointer px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400 sm:px-6 lg:table-cell"
          onClick={() => onSort('category')}
        >
          {t('transaction.category')} {renderSortIcon('category')}
        </th>
        <th
          className="hidden cursor-pointer px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400 sm:px-6 lg:table-cell"
          onClick={() => onSort('destination')}
        >
          <div className="flex items-center gap-2">
            <span>{t('transaction.fromTo')}</span>
            {renderSortIcon('destination')}
            <div className="group relative">
              <div className="flex items-center justify-center rounded-full p-1 text-slate-400 hover:text-slate-500 dark:text-slate-500 dark:hover:text-slate-400">
                <InformationCircleIcon className="h-4 w-4" />
              </div>
              <div className="invisible absolute left-0 top-full z-10 mt-2 w-72 rounded-lg bg-white p-2 text-sm normal-case text-slate-500 shadow-lg transition-all group-hover:visible dark:bg-surface-700 dark:text-slate-300">
                {t('transaction.fromToHelp')}
              </div>
            </div>
          </div>
        </th>
        <th
          className="cursor-pointer px-3 py-3 text-right text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400 sm:px-6"
          onClick={() => onSort('amount')}
        >
          {t('transaction.amount')} {renderSortIcon('amount')}
        </th>
        {showActions && (
          <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
            {t('common.actions')}
          </th>
        )}
      </tr>
    </thead>
  );
};
